"use client";

import { SiteHero, Ladder, SplitRow, SectionHead, FullBleedPhoto, FaqList, CtaBand, RelatedLinks } from "@/components/marketing/site-ui";
import { EventDiscoveryMock } from "@/components/marketing/site-mocks";
import { useSiteContent } from "@/components/marketing/use-site-content";

/**
 * The hosting limits are read from the same place the product enforces them, so the page
 * cannot say "one a month" after someone changes the plan. See lib/data/subscriptions.ts.
 */
import { plans } from "@/lib/data/subscriptions";

function hostingLimits() {
  const free = plans.find((plan) => plan.id === "free");
  const premium = plans.find((plan) => plan.id === "premium-monthly");
  const elite = plans.find((plan) => plan.id === "elite-monthly");
  return [free, premium, elite];
}

export function EventsClient() {
  const c = useSiteContent();
  const page = c.eventsPage;
  const [free, premium, elite] = hostingLimits();

  const tiers = [
    // "1 events a month" is the kind of thing that makes a page look unfinished, so the
    // singular gets its own string rather than being pluralised by a rule that will not
    // survive translation into 21 languages.
    { name: free?.name ?? "Free", value: "1", label: page.limitsPerMonthOne },
    { name: premium?.name ?? "Premium", value: "2", label: page.limitsPerMonth },
    { name: elite?.name ?? "Elite", value: page.limitsUnlimited, label: page.limitsPerMonth },
  ];

  return (
    <>
      <SiteHero
        eyebrow={page.eyebrow}
        title={page.title}
        lede={page.lede}
        primaryLabel={page.primary}
        note={page.note}
        aside={<EventDiscoveryMock />}
      />

      <section className="border-y border-neutral-200 bg-white py-14 md:py-20">
        <div className="gs-page">
          <Ladder steps={page.ladder} />
        </div>
      </section>

      <FullBleedPhoto
        src="/images/events-roundtable.webp"
        alt={page.photoAlt}
        eyebrow={page.photoEyebrow}
        title={page.photoTitle}
        body={page.photoBody}
      />

      <section className="py-16 md:py-24">
        <div className="gs-page">
          <SplitRow
            eyebrow={page.sellEyebrow}
            title={page.sellTitle}
            body={page.sellBody}
            items={page.sellItems}
            aside={
              /* The hosting allowance, as three numbers rather than a paragraph. It is the
                 single fact a prospective host wants, and it is the one most people get wrong
                 about GrowthSync: hosting is not behind the paywall, volume is. */
              <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-7 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.28)] md:p-9">
                <p className="gs-eyebrow text-primary">{page.limitsEyebrow}</p>
                <h3 className="mt-3 text-balance gs-title text-neutral-900">{page.limitsTitle}</h3>
                <dl className="mt-7 divide-y divide-neutral-200 border-y border-neutral-200">
                  {tiers.map((tier) => (
                    <div key={tier.name} className="flex items-baseline justify-between gap-4 py-4">
                      <dt className="text-[15px] font-semibold text-neutral-700">{tier.name}</dt>
                      <dd className="text-right">
                        <span className="text-2xl font-extrabold tracking-[-0.03em] text-neutral-900">{tier.value}</span>
                        <span className="ml-2 text-[13px] text-neutral-500">{tier.label}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-5 text-[14px] leading-relaxed text-neutral-600">{page.limitsBody}</p>
              </div>
            }
          />
        </div>
      </section>

      <section className="border-t border-neutral-200 py-16 md:py-20">
        <div className="gs-page">
          {/* The measure goes on an inner box: .gs-page centres, so a narrower max-width on it
              would recentre this section and move the page's left edge. */}
          <div className="max-w-4xl">
            <SectionHead title={c.common.faqHeading} align="left" />
            <div className="mt-8">
              <FaqList items={page.faq} />
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks
        links={[
          { href: "/features/discover", title: c.features.discover.name, body: c.features.discover.tagline },
          { href: "/features/communities", title: c.features.communities.name, body: c.features.communities.tagline },
          { href: "/pricing", title: c.nav.pricing, body: c.pricingPage.short },
        ]}
      />

      <div className="pt-14 md:pt-20">
        <CtaBand />
      </div>
    </>
  );
}
